import React, { useEffect, useState, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { PlusCircleIcon, ChevronLeftIcon, TrashIcon } from "@heroicons/react/24/outline"
import { withKPIRows, statValue, formatRevenue, CopyId, sortArg, confirmDelete, linkCell, NameToggleCell, useAdsKpiPing } from "./KPITiles"
import KPIDashboard from "./KPIDashboard"
import AdView from "./AdView"
import { CampaignCRUD, AdCRUD, ListAccounts } from "../api"

/** KPI columns appended to campaigns/ads tables. Sortable — the loader
 *  (withKPIRows) attaches numeric _kpi_* fields and sorts/paginates on them.
 *  Display still formats from the raw _stats (handles the 'both' touch). */
export const kpiColumns = () => [
  { id: "_kpi_impressions", title: "Impr.", sortable: true, render: (_, i) => i?._kpi_impressions ?? 0 },
  { id: "_kpi_clicks", title: "Clicks", sortable: true, render: (_, i) => i?._kpi_clicks ?? 0 },
  { id: "_kpi_ctr", title: "CTR", sortable: true, render: (_, i) => `${i?._kpi_ctr ?? 0}%` },
  { id: "_kpi_signups", title: "Signups", sortable: true, render: (_, i) => statValue(i?._stats, "signups") },
  { id: "_kpi_revenue", title: "Revenue", sortable: true, render: (_, i) => formatRevenue(i?._stats?.revenue) },
]

const statusCell = (v) => (
  <span className={v === "active" ? "text-admin_success font-semibold" : "text-admin_text_secondary"}>{v}</span>
)

/**
 * Ads dashboard — walks the concept graph downwards:
 *   account (selector) → campaigns table → campaign (ads) → ad (assets).
 * Every level: KPI tiles + per-row KPI columns, date range picker in the
 * header next to the action buttons. Remote (connected) accounts are
 * read-only; their data is proxied from the origin install.
 */
const BASE = "/admin/ads/dashboard"
const readParams = () => {
  if (typeof window === "undefined") return {}
  const s = new URLSearchParams(window.location.search)
  return { account: s.get("account"), campaign: s.get("campaign"), ad: s.get("ad") }
}
const buildUrl = ({ account, campaign, ad }) => {
  const s = new URLSearchParams()
  if (account) s.set("account", account)
  if (campaign) s.set("campaign", campaign)
  if (ad) s.set("ad", ad)
  const q = s.toString()
  return BASE + (q ? `?${q}` : "")
}

const Dashboard = () => {
  const [accounts, setAccounts] = useState([])
  const [remoteTree, setRemoteTree] = useState(null)
  const [campaignDoc, setCampaignDoc] = useState(null)
  const [adDoc, setAdDoc] = useState(null)
  const [refresh, setRefresh] = useState(0)
  const [dateRange, setDateRange] = useState({ type: "preset", value: { days: 30 } })

  // Realtime: reload the campaigns table (per-row KPIs) on ad events.
  useAdsKpiPing(useCallback(() => setRefresh((r) => r + 1), []))

  // URL is the source of truth for the drill-down — survives refresh + back/fwd.
  const [params, setParamsState] = useState(readParams())
  const go = (next, replace = false) => {
    setParamsState(next)
    if (typeof window !== "undefined") {
      const url = buildUrl(next)
      replace ? window.history.replaceState({}, "", url) : window.history.pushState({}, "", url)
    }
  }
  useEffect(() => {
    const onPop = () => setParamsState(readParams())
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  const account = accounts.find((a) => a._id === params.account) || null
  const isRemote = account?.type === "remote"
  const remoteArg = isRemote ? account._id : undefined
  const view = params.ad ? "ad" : params.campaign ? "campaign" : "campaigns"

  // load accounts; default the account param to local on first visit
  useEffect(() => {
    ;(async () => {
      const res = await ListAccounts().catch(() => null)
      const list = res?.accounts || []
      setAccounts(list)
      if (!readParams().account && list.length) {
        const def = list.find((a) => a.type === "local") || list[0]
        go({ ...readParams(), account: def._id }, true)
      }
    })()
  }, [])

  // remote account → pull its campaign tree through the proxy
  useEffect(() => {
    if (!account || account.type !== "remote") { setRemoteTree(null); return }
    let alive = true
    ;(async () => {
      const res = await ListAccounts(account._id).catch(() => null)
      if (!alive) return
      if (res?.expanded?.error) {
        showNotification({ message: "Connected account unreachable", className: "bg-admin_error text-admin_text_inv" })
        setRemoteTree(null)
        return
      }
      setRemoteTree(res?.expanded || null)
    })()
    return () => { alive = false }
  }, [params.account, accounts.length])

  // resolve the campaign doc for the id in the URL (refresh-safe)
  useEffect(() => {
    if (!params.campaign) { setCampaignDoc(null); return }
    let alive = true
    ;(async () => {
      if (isRemote) { setCampaignDoc((remoteTree?.campaigns || []).find((c) => c._id === params.campaign) || null); return }
      const res = await CampaignCRUD.Get({ match: { _id: params.campaign }, page: 1, itemsPerPage: 1 }).catch(() => null)
      if (alive) setCampaignDoc(res?.data?.[0] || null)
    })()
    return () => { alive = false }
  }, [params.campaign, isRemote, remoteTree, refresh])

  // resolve the ad doc for the id in the URL
  useEffect(() => {
    if (!params.ad) { setAdDoc(null); return }
    let alive = true
    ;(async () => {
      if (isRemote) { setAdDoc((remoteTree?.ads || []).find((a) => a._id === params.ad) || null); return }
      const res = await AdCRUD.Get({ match: { _id: params.ad }, page: 1, itemsPerPage: 1 }).catch(() => null)
      if (alive) setAdDoc(res?.data?.[0] || null)
    })()
    return () => { alive = false }
  }, [params.ad, isRemote, remoteTree])

  const newCampaign = async () => {
    const name = window.prompt("Campaign name")
    if (!name) return
    await CampaignCRUD.Create({ account: account._id, name, status: "active" })
    setRefresh((r) => r + 1)
  }

  const toggleCampaignServing = async (item, on) => {
    await CampaignCRUD.Update({ _id: item._id, status: on ? "active" : "stopped" })
    setRefresh((r) => r + 1)
  }

  const loadCampaigns = useCallback(async (p) => {
    let rows
    if (isRemote) {
      rows = remoteTree?.campaigns || []
    } else if (account) {
      // fetch all (KPI sort needs the full set); withKPIRows paginates.
      // crud get requires an explicit page — omitting it returns nothing.
      const res = await CampaignCRUD.Get({
        match: { account: account._id, deleted: { $ne: true } },
        page: 1,
        itemsPerPage: 1000,
        sort: sortArg(p.sort),
      })
      rows = res?.data || []
    } else {
      rows = []
    }
    return withKPIRows({ rows, level: "campaign", account: remoteArg, range: dateRange, params: p })
  }, [account, isRemote, remoteTree, refresh, remoteArg, dateRange])

  if (!account) {
    return <div className="p-6"><Typography variant="admin_body1">Loading accounts…</Typography></div>
  }

  return (
    <div className="p-6 flex flex-col gap-5">
      {/* header: account selector + date range + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Typography variant="admin_h3">Aldoo Ads</Typography>
          <select
            className="border border-admin_bordered rounded-lg px-3 py-2 bg-paper"
            value={account._id}
            onChange={(e) => go({ account: e.target.value })}
          >
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name} {a.type === "remote" ? "(connected)" : "(this site)"}
              </option>
            ))}
          </select>
        </div>
        {view === "campaigns" && (
          <div className="flex items-center gap-2">
            <DateFilterDropdown
              showLabel={false}
              value={dateRange}
              onChange={setDateRange}
              variant="admin"
              localUserPreferencesKey="admin.ads.dateFilter"
            />
            {!isRemote && (
              <Button variant="admin_primary" onClick={newCampaign}>
                <PlusCircleIcon className="w-5 h-5 mr-1" /> New Campaign
              </Button>
            )}
          </div>
        )}
      </div>

      {view !== "campaigns" && (
        <button
          className="flex items-center gap-1 text-admin_text_secondary text-sm w-fit"
          onClick={() => go(view === "ad"
            ? { account: params.account, campaign: params.campaign }
            : { account: params.account })}
        >
          <ChevronLeftIcon className="w-4 h-4" />
          {view === "ad" ? `Back to ${campaignDoc?.name || "campaign"}` : "Back to campaigns"}
        </button>
      )}

      {view === "campaigns" && (
        <>
          <KPIDashboard level="account" id={account._id} account={remoteArg} range={dateRange} />
          <DataTable
            key={`campaigns-${account._id}-${refresh}-${isRemote ? (remoteTree ? "r" : "l") : "x"}-${JSON.stringify(dateRange)}`}
            title="Campaigns"
            apiMethod={loadCampaigns}
            itemsPerPage={25}
            onRowClick={(item) => go({ account: params.account, campaign: item._id })}
            renders={[
              {
                id: "name", title: "Campaign", sortable: true,
                render: (v, item) => (
                  <NameToggleCell
                    name={v}
                    isOn={item.status === "active"}
                    onToggle={isRemote ? undefined : (on) => toggleCampaignServing(item, on)}
                  />
                ),
              },
              { id: "status", title: "Status", sortable: true, render: statusCell },
              ...kpiColumns(),
              { id: "created", title: "Created", sortable: true, render: (v) => (v ? new Date(v).toLocaleDateString() : "") },
            ]}
          />
        </>
      )}

      {view === "campaign" && (
        campaignDoc ? (
          <CampaignView
            account={account}
            isRemote={isRemote}
            remoteTree={remoteTree}
            campaign={campaignDoc}
            onOpenAd={(ad) => go({ account: params.account, campaign: params.campaign, ad: ad._id })}
            onDeleted={() => { go({ account: params.account }); setRefresh((r) => r + 1) }}
          />
        ) : <Typography variant="admin_body1">Loading campaign…</Typography>
      )}

      {view === "ad" && (
        adDoc ? (
          <AdView
            account={account}
            isRemote={isRemote}
            campaign={campaignDoc}
            ad={adDoc}
            onDeleted={() => go({ account: params.account, campaign: params.campaign })}
          />
        ) : <Typography variant="admin_body1">Loading ad…</Typography>
      )}
    </div>
  )
}

// ── campaign level: KPIs + ads table ─────────────────────────────────────────
const CampaignView = ({ account, isRemote, remoteTree, campaign, onOpenAd, onDeleted }) => {
  const [refresh, setRefresh] = useState(0)
  const [dateRange, setDateRange] = useState({ type: "preset", value: { days: 30 } })
  const remoteArg = isRemote ? account._id : undefined
  // Realtime: reload the ads table (per-row KPIs) on ad events.
  useAdsKpiPing(useCallback(() => setRefresh((r) => r + 1), []))

  const loadAds = useCallback(async (params) => {
    let rows
    if (isRemote) {
      rows = (remoteTree?.ads || []).filter((a) => a.campaign === campaign._id)
    } else {
      const res = await AdCRUD.Get({
        match: { campaign: campaign._id, deleted: { $ne: true } },
        page: 1,
        itemsPerPage: 1000,
        sort: sortArg(params.sort),
      })
      rows = res?.data || []
    }
    return withKPIRows({ rows, level: "ad", account: remoteArg, range: dateRange, params })
  }, [campaign, isRemote, remoteTree, refresh, remoteArg, dateRange])

  const toggleCampaign = async () => {
    const status = campaign.status === "active" ? "stopped" : "active"
    await CampaignCRUD.Update({ _id: campaign._id, status })
    campaign.status = status
    setRefresh((r) => r + 1)
  }

  const newAd = async () => {
    const name = window.prompt("Ad name")
    if (!name) return
    await AdCRUD.Create({ campaign: campaign._id, name, status: "active", cta_url: "", variables: {} })
    setRefresh((r) => r + 1)
  }

  const toggleAdServing = async (item, on) => {
    await AdCRUD.Update({ _id: item._id, status: on ? "active" : "stopped" })
    setRefresh((r) => r + 1)
  }

  const deleteCampaign = () =>
    confirmDelete(campaign.name, async () => {
      await CampaignCRUD.SoftDelete({ _id: campaign._id })
      showNotification({ message: "Campaign deleted", className: "bg-admin_success text-admin_text_inv" })
      onDeleted?.()
    })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Typography variant="admin_h3">{campaign.name}</Typography>
        <div className="flex items-center gap-2">
          <DateFilterDropdown
            showLabel={false}
            value={dateRange}
            onChange={setDateRange}
            variant="admin"
            localUserPreferencesKey="admin.ads.campaign.dateFilter"
          />
          {!isRemote && (
            <>
              <Button variant="admin_secondary" onClick={toggleCampaign}>
                {campaign.status === "active" ? "Stop campaign" : "Start campaign"}
              </Button>
              <Button variant="admin_primary" onClick={newAd}>
                <PlusCircleIcon className="w-5 h-5 mr-1" /> New Ad
              </Button>
              <Button variant="admin_error" onClick={deleteCampaign}>
                <TrashIcon className="w-5 h-5 mr-1" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>
      <KPIDashboard level="campaign" id={campaign._id} account={remoteArg} range={dateRange} />
      <DataTable
        key={`ads-${campaign._id}-${refresh}-${JSON.stringify(dateRange)}`}
        title="Ads"
        apiMethod={loadAds}
        itemsPerPage={25}
        onRowClick={onOpenAd}
        renders={[
          {
            id: "name", title: "Ad", sortable: true,
            render: (v, item) => (
              <NameToggleCell
                name={v}
                isOn={item.status === "active"}
                onToggle={isRemote ? undefined : (on) => toggleAdServing(item, on)}
              />
            ),
          },
          { id: "_id", title: "Ad ID", render: (v) => <CopyId id={v} /> },
          { id: "status", title: "Status", sortable: true, render: statusCell },
          ...kpiColumns(),
          { id: "created", title: "Created", sortable: true, render: (v) => (v ? new Date(v).toLocaleDateString() : "") },
        ]}
      />
    </div>
  )
}

export default Dashboard
